/**
 * Registro de los templates CUSTOM (hand-made) de la app nativa y de los bundle IDs que los anclan.
 *
 * CONTRATO admin ↔ `seguridad-boton-nativo`. Espeja `Clientes.forBundleId`
 * (`shared/.../domain/model/Cliente.kt`) y las variantes que rutea `InicioDispatcher.kt`.
 * El test `ClienteTemplateTest.kt` del nativo es el que fija esa tabla: si cambia allá, cambia acá.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 * --------------------------
 * La app nativa tiene dos formas de dibujar el inicio:
 *
 *  1. **Templates personalizados**: Composables escritos a mano para un municipio puntual
 *     (Quilmes, Hurlingham, Lanús, …). NO se eligen desde el admin: el nativo los resuelve por el
 *     BUNDLE ID de la app (`Clientes.forBundleId`), que es la única fuente. `cliente.inicioTemplate`
 *     del backend solo admite las genéricas.
 *  2. **Botoneras genéricas** (`grid` / `banner` / `centrado` / `hub`): la vista que se arma sola,
 *     para clientes nuevos o de prueba que todavía no tienen una pantalla propia.
 *
 * El problema que resuelve: en el nativo la categoría GANA sobre el cliente
 * (`InicioViewModel`: `cat.inicioTemplate ?: cli.inicioTemplate`), y el cliente gana sobre el seed
 * del bundle ID (`buildClienteRuntime`). O sea: poner una botonera genérica en la categoría (o en el
 * cliente) de un municipio con template propio le REEMPLAZA la pantalla hecha a mano. Con esta tabla
 * el admin sabe qué clientes están en esa situación y puede bloquear la elección en vez de avisar
 * después.
 */

/** Un template hecho a mano en la app nativa. */
export interface TemplateCustom {
  /** Valor de `inicioTemplate` que usa el nativo (minúsculas, con espacios). */
  id: string;
  /** Nombre para mostrar en el admin. */
  label: string;
  /** Bundle IDs que lo anclan (Android `com.…` + iOS `ar.…`). */
  bundleIds: string[];
  /**
   * Valores de `categoria.nombreTemplate` que el dispatcher distingue para este template. La
   * primera es la que se usa cuando el campo está vacío. `[]` = el template ignora la variante
   * (una sola pantalla para todas las categorías).
   */
  variantes: string[];
  /**
   * Slots de imagen de `ICategoriaCliente` que el template REALMENTE dibuja (claves de
   * `SLOTS_NATIVOS`). Salen de grepear `state.<slot>` en cada `*Template.kt`. Los que no están
   * se guardan igual en la base pero no se ven en ningún lado: el editor los deja cargar y avisa.
   */
  slots: string[];
  /** Qué cambia entre variantes / cómo se comporta. Se muestra como ayuda. */
  nota?: string;
}

/**
 * Tabla de `Clientes.forBundleId` con `inicioTemplate` ≠ "default". Los bundle IDs que NO están acá
 * (incluidos `com.gpesistemas.chascomus` y `com.grupocontrol.app`) caen a la familia DEFAULT y por
 * lo tanto SÍ pueden usar una botonera genérica.
 */
export const TEMPLATES_CUSTOM: TemplateCustom[] = [
  {
    id: 'quilmes',
    label: 'Quilmes (Mi Alerta Quilmes)',
    bundleIds: [
      'com.gpesistemas.mialertaquilmes',
      'ar.gpesistemas.mialertaquilmes',
    ],
    slots: ['arriba', 'abajo', 'fondo', 'sirena', 'reflector', 'adelante', 'atras'],
    variantes: ['normal', 'violencia de genero'],
    nota: 'normal: 3 páginas (normal / aporte / aporte urbano). violencia de genero: 2 páginas.',
  },
  {
    id: 'la matanza',
    label: 'La Matanza (Alerta Ciudadana)',
    bundleIds: [
      'com.gpesistemas.alertaciudadana',
      'ar.gpesistemas.alertaciudadana',
    ],
    slots: ['arriba', 'sirena', 'reflector'],
    variantes: [],
    nota: 'Una sola página para todas las categorías; el tercer botón sale de la config.',
  },
  {
    id: 'avellaneda',
    label: 'Avellaneda (Alarma Comunitaria)',
    bundleIds: [
      'com.gpesistemas.alarmacomunitaria',
      'ar.gpesistemas.alarmacomunitaria',
    ],
    slots: ['arriba', 'abajo', 'fondo', 'sirena', 'reflector'],
    variantes: ['normal', 'violencia de genero'],
    nota: 'normal: principales + sirena + reflector apilados. Cualquier otra variante: banner de categoría (imagen "abajo") + fila de sirena/reflector.',
  },
  {
    id: 'almirante brown',
    label: 'Almirante Brown (Somos Brown)',
    bundleIds: ['com.gpesistemas.somosbrown', 'ar.gpesistemas.somosbrown'],
    slots: ['arriba', 'fondo', 'sirena', 'reflector', 'adelante', 'atras'],
    variantes: [],
    nota: '2 páginas (principales / secundarios) + telemedicina. No usa la variante.',
  },
  {
    id: 'lomas de zamora',
    label: 'Lomas de Zamora (Seguridad Lomas)',
    bundleIds: [
      'com.gpesistemas.seguridadlomas',
      'ar.gpesistemas.seguridadlomas',
    ],
    slots: ['arriba', 'fondo', 'sirena', 'adelante', 'atras'],
    variantes: ['normal', 'violencia de genero'],
    nota: 'normal: 1 página. violencia de genero: 2 páginas.',
  },
  {
    id: 'hurlingham',
    label: 'Hurlingham (#HurlinghamSegura)',
    bundleIds: [
      'com.gpesistemas.hurlinghamsegura',
      'ar.gpesistemas.hurlinghamsegura',
    ],
    slots: ['arriba', 'abajo', 'fondo', 'sirena', 'reflector', 'atras'],
    variantes: [
      'normal',
      'violencia de genero',
      'comercio seguro',
      'escuelas seguras',
      'repartidores seguros',
      'universidades seguras',
    ],
    nota: 'normal: 2 páginas. El resto: una pantalla temática por variante.',
  },
  {
    id: 'ezeiza',
    label: 'Ezeiza (Ezeiza Segura)',
    bundleIds: ['com.gpesistemas.ezeizasegura', 'ar.gpesistemas.ezeizasegura'],
    slots: ['arriba', 'fondo', 'sirena', 'reflector'],
    variantes: [],
    nota: 'Hub tipo menú: los botones con función "Menu" de la pantalla 1 navegan a su grupo destino.',
  },
  {
    id: 'lujan',
    label: 'Luján (Alerta Luján)',
    bundleIds: ['com.gpesistemas.lujanalerta24', 'ar.gpesistemas.lujanalerta24'],
    slots: ['arriba', 'abajo', 'fondo', 'sirena', 'reflector'],
    variantes: [],
    nota: 'Una pantalla config-driven. No usa la variante.',
  },
  {
    id: 'lanus',
    label: 'Lanús (Alerta Lanús)',
    bundleIds: ['com.gpesistemas.lanussegura', 'ar.gpesistemas.lanussegura'],
    // `abajo` y `atras` los usa por las páginas 2 y 3, que delegan a la familia default.
    slots: ['arriba', 'abajo', 'fondo', 'sirena', 'reflector', 'adelante', 'atras'],
    variantes: ['normal', 'violencia de genero'],
    nota: 'normal: banner grande + fila Reflector/Chat/Sirena (páginas 2 y 3 caen a la familia default). violencia de genero: una sola página.',
  },
];

/** Índice bundleId (normalizado) → template, para resolver en O(1). */
const POR_BUNDLE_ID = new Map<string, TemplateCustom>(
  TEMPLATES_CUSTOM.flatMap((t) => t.bundleIds.map((b) => [b, t] as const)),
);

const normalizar = (valor?: string | null): string =>
  (valor ?? '').trim().toLowerCase();

/**
 * ¿Este cliente tiene template hecho a mano en la app? Se resuelve por bundle ID, igual que el
 * nativo — NO por nombre: la inferencia por nombre (`TEMPLATES_PORTADOS`) se eliminó del nativo.
 * Alcanza con que coincida el de Android o el de iOS.
 */
export function templateCustomDeCliente(cliente?: {
  bundleIdAndroid?: string;
  bundleIdIOS?: string;
}): TemplateCustom | undefined {
  if (!cliente) return undefined;
  return (
    POR_BUNDLE_ID.get(normalizar(cliente.bundleIdAndroid)) ??
    POR_BUNDLE_ID.get(normalizar(cliente.bundleIdIOS))
  );
}

/** ¿Este template dibuja esa imagen? Si no, cargarla no tiene efecto (pero se guarda igual). */
export function templateUsaSlot(
  template: TemplateCustom | undefined,
  slot: string,
): boolean {
  // Sin template custom no hay nada que descartar: manda la spec de la botonera genérica.
  if (!template) return true;
  return template.slots.includes(slot);
}

/** El mismo lookup, por un id de template ya conocido (p.ej. el que quedó guardado en el cliente). */
export function templateCustomPorId(id?: string | null): TemplateCustom | undefined {
  const buscado = normalizar(id);
  return TEMPLATES_CUSTOM.find((t) => t.id === buscado);
}
